// Тосты пульта (задача D5, ui-spec §5): один `<Toaster />` в каркасе и три вида уведомлений.
//
// - Успех — `toast.success(…)`, держится 4 с (умолчание Toaster).
// - «Отменить» — `toastUndo`: 8 с в разборе, 10 с в «Запуске» (длительность передаёт экран).
// - Ошибка — `toastError`: висит, пока не закроют, с «Повторить», если повтор возможен. Ошибка действия —
//   `toastActionError`: при обрыве связи без «Повторить» — он уже в баннере «Нет связи» (UX-ревью D5–D7).
// - Область тостов — `aria-live="polite"`. Остановку Анны объявляет пилюля статуса (`assertive`), а не тост.
import { toast } from 'sonner';
import { messageRuOf, ServiceUnavailableError } from '@/api/errors';
import { Toaster } from '@/components/ui/sonner';
import { useViewport } from '@/lib/viewport';

/** Сколько держится тост успеха, мс. */
export const TOAST_SUCCESS_MS = 4000;

export function AppToaster() {
  // На телефоне тост закрывают смахиванием, а маленький крестик в углу (20 px) — меньше касания 40 px (Ruling 52).
  const phone = useViewport() === 'phone';
  return <Toaster duration={TOAST_SUCCESS_MS} closeButton={!phone} />;
}

/** Тост с «Отменить»: действие уже сделано, его можно вернуть, пока тост на экране. */
export function toastUndo(o: { readonly message: string; readonly onUndo: () => void; readonly durationMs: number }): string | number {
  return toast(o.message, {
    duration: o.durationMs,
    action: {
      label: 'Отменить',
      onClick: () => {
        o.onUndo();
      },
    },
  });
}

/** Ошибка: висит до закрытия; с `onRetry` — кнопка «Повторить»; с `id` — заменяет тот тост (например, «Проверяем…»). */
export function toastError(message: string, onRetry?: () => void, o: { readonly id?: string | number } = {}): string | number {
  return toast.error(message, {
    ...(o.id === undefined ? {} : { id: o.id }),
    duration: Number.POSITIVE_INFINITY,
    ...(onRetry === undefined
      ? {}
      : {
          action: {
            label: 'Повторить',
            onClick: () => {
              onRetry();
            },
          },
        }),
  });
}

/**
 * Ошибка действия: «{что не удалось}: {текст}». «Повторить» — когда сервис ответил ошибкой; сервис не ответил
 * (`ServiceUnavailableError`) — без кнопки: повтор уже есть в баннере «Нет связи», второй рядом не нужен.
 */
export function toastActionError(
  leadRu: string,
  error: unknown,
  retry: () => void,
  o: { readonly id?: string | number } = {},
): string | number {
  const text = `${leadRu}: ${messageRuOf(error)}`;
  return error instanceof ServiceUnavailableError ? toastError(text, undefined, o) : toastError(text, retry, o);
}
